import { queryOptions, skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { groupsQueryOptions } from '@/hooks/contacts/use-groups';
import { type GroupInfo, groupInfoScheme } from '@/models/group/group-info';
import { ResponseError } from '@/models/response';
import ROUTES from '@/routes';
import { addTranslateToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { isGroupAdmin, isGroupMemberAdmin } from '@/utils/group';

export const generateGroupInfoQueryOptions = (groupId: Nullable<number>) =>
  queryOptions({
    queryKey: ['/GetGroupInfoByGroupID', groupId],
    queryFn: groupId
      ? ({ signal }) =>
          fetcher<GroupInfo>(
            '/GetGroupInfoByGroupID',
            { GroupId: groupId },
            { signal, schema: groupInfoScheme, validResponseCodes: [0, 1] },
          )
      : skipToken,
  });

export default function useGroupInfo(groupId: Nullable<number>) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery(generateGroupInfoQueryOptions(groupId));

  useEffect(() => {
    if (query.data?.GroupData) {
      const groupData = query.data.GroupData;
      queryClient.setQueryData(groupsQueryOptions.queryKey, (state) =>
        produce(state, (draft) => {
          if (!groupId || !draft?.[groupId]) return;

          draft[groupId] = {
            ...groupData,
            Order: draft[groupId].Order ?? Object.keys(draft).length,
          };
        }),
      );
    }
  }, [groupId, query.data?.GroupData]);

  useEffect(() => {
    if (query.error && ResponseError.isResponseError(query.error)) {
      if (query.error.code === 1) {
        addTranslateToast({
          type: 'error',
          message: 'conversation.session-not-available',
        });
        queryClient.invalidateQueries(groupsQueryOptions).catch(console.error);
        navigate(ROUTES.MESSAGES.$path());
      }
    }
  }, [query.error]);

  const canManage = isGroupAdmin(query.data);

  const checkIsMemberAdmin = (targetUserId: number): boolean => {
    return isGroupMemberAdmin(query.data?.Members, targetUserId);
  };

  const checkCanAddFriend = (targetUserId: number): boolean => {
    if (!groupId) return true;

    return (
      query.data?.GroupData?.AddFriend ||
      isGroupAdmin(query.data) ||
      isGroupMemberAdmin(query.data?.Members, targetUserId)
    );
  };

  const checkCanMessage = async () => {
    await queryClient.invalidateQueries(generateGroupInfoQueryOptions(groupId));
    const data = await queryClient.fetchQuery(generateGroupInfoQueryOptions(groupId));

    const canManage = isGroupAdmin(data);
    const canMessage = canManage ? data?.Self?.Status === 1 : !(data?.GroupData?.AllBan || data?.Self?.Status === 0);

    addTranslateToast({
      type: canMessage ? 'success' : 'error',
      message: canMessage ? 'conversation.placeholder-unbanned' : 'conversation.placeholder-banned',
    });

    return canMessage;
  };

  return {
    checkIsMemberAdmin,
    checkCanAddFriend,
    checkCanMessage,
    canManage,
    canMessage: canManage
      ? query.data?.Self?.Status === 1
      : !(query.data?.GroupData?.AllBan || query.data?.Self?.Status === 0),
    isAdmin: query.data?.Self?.Role === 10,
    isChannel: query.data?.GroupData?.GroupType === 1,
    isPublicGroup: query.data?.GroupData?.JoinType === 0,
    ...query,
  } as const;
}
